<script setup lang="ts">
  import { ref } from 'vue'

  interface Props {
    visible?: boolean
    widgetOpen?: boolean
  }

  withDefaults(defineProps<Props>(), {
    visible: false,
    widgetOpen: false
  })

  const emit = defineEmits<{
    (e: 'click'): void
  }>()

  const isHovered = ref(false)
  const isPressed = ref(false)

  const PRESS_DURATION_MS = 100
  let pressedAt = 0
  let pressTimer: ReturnType<typeof setTimeout> | null = null

  const releasePress = () => {
    isPressed.value = false
    pressTimer = null
  }

  const scheduleRelease = () => {
    if (pressTimer) return
    const elapsed = Date.now() - pressedAt
    const remaining = PRESS_DURATION_MS - elapsed
    if (remaining <= 0) {
      releasePress()
    } else {
      pressTimer = setTimeout(releasePress, remaining)
    }
  }

  const handleMouseDown = () => {
    if (pressTimer) {
      clearTimeout(pressTimer)
      pressTimer = null
    }
    pressedAt = Date.now()
    isPressed.value = true
  }

  const handleMouseUp = () => {
    scheduleRelease()
  }

  const handleMouseLeave = () => {
    isHovered.value = false
    scheduleRelease()
  }

  const handleClick = () => {
    emit('click')
  }
</script>

<template>
  <Transition name="launcher-fade">
    <button
      v-if="visible"
      class="launcher-button"
      :class="{
        'launcher-button--hovered': isHovered,
        'launcher-button--pressed': isPressed,
        'launcher-button--widget-open': widgetOpen
      }"
      :aria-label="widgetOpen ? 'Close AI Assistant' : 'Open AI Assistant'"
      @mouseenter="isHovered = true"
      @mouseleave="handleMouseLeave"
      @mousedown="handleMouseDown"
      @mouseup="handleMouseUp"
      @click="handleClick"
    >
      <div class="launcher-button__inner">
        <!-- Custom sparkle icon matching designer SVG -->
        <svg
          class="launcher-button__icon"
          width="34"
          height="34"
          viewBox="0 0 249 249"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <!-- Main large 4-pointed star -->
          <path
            d="M92.9394 158.984C92.1044 155.747 90.4173 152.793 88.0536 150.429C85.6899 148.066 82.736 146.379 79.4993 145.544L22.1193 130.747C21.1403 130.469 20.2787 129.88 19.6652 129.068C19.0517 128.256 18.7197 127.266 18.7197 126.249C18.7197 125.231 19.0517 124.241 19.6652 123.429C20.2787 122.617 21.1403 122.028 22.1193 121.75L79.4993 106.944C82.7349 106.11 85.688 104.424 88.0516 102.062C90.4151 99.7004 92.1029 96.7485 92.9394 93.5134L107.736 36.1334C108.011 35.1506 108.6 34.2847 109.413 33.6679C110.226 33.0512 111.218 32.7173 112.239 32.7173C113.26 32.7173 114.252 33.0512 115.065 33.6679C115.878 34.2847 116.467 35.1506 116.742 36.1334L131.529 93.5134C132.364 96.7502 134.052 99.7041 136.415 102.068C138.779 104.431 141.733 106.119 144.97 106.954L202.35 121.74C203.336 122.013 204.206 122.601 204.827 123.415C205.447 124.23 205.782 125.225 205.782 126.249C205.782 127.272 205.447 128.267 204.827 129.082C204.206 129.896 203.336 130.484 202.35 130.757L144.97 145.544C141.733 146.379 138.779 148.066 136.415 150.429C134.052 152.793 132.364 155.747 131.529 158.984L116.733 216.364C116.458 217.346 115.869 218.212 115.056 218.829C114.243 219.446 113.25 219.78 112.23 219.78C111.209 219.78 110.217 219.446 109.403 218.829C108.59 218.212 108.001 217.346 107.726 216.364L92.9394 158.984Z"
            fill="white"
          />
          <!-- Small 4-pointed star (top-right) -->
          <path
            d="M198.132 44.8934C197.881 43.9224 197.375 43.0363 196.666 42.3272C195.957 41.6181 195.071 41.112 194.1 40.8615L176.887 36.4228C176.593 36.3394 176.334 36.1626 176.15 35.919C175.966 35.6755 175.867 35.3785 175.867 35.0732C175.867 34.7679 175.966 34.471 176.15 34.2274C176.334 33.9839 176.593 33.807 176.887 33.7237L194.1 29.2821C195.071 29.0319 195.956 28.5262 196.665 27.8176C197.374 27.1091 197.881 26.2235 198.132 25.2531L202.57 8.0397C202.653 7.74486 202.83 7.48511 203.074 7.30008C203.318 7.11505 203.615 7.01489 203.921 7.01489C204.228 7.01489 204.525 7.11505 204.769 7.30008C205.013 7.48511 205.19 7.74486 205.272 8.0397L209.708 25.2531C209.959 26.224 210.465 27.1102 211.174 27.8193C211.883 28.5283 212.769 29.0344 213.74 29.2849L230.954 33.7208C231.25 33.8025 231.511 33.979 231.697 34.2233C231.883 34.4676 231.983 34.7662 231.983 35.0732C231.983 35.3803 231.883 35.6789 231.697 35.9232C231.511 36.1674 231.25 36.344 230.954 36.4256L213.74 40.8615C212.769 41.112 211.883 41.6181 211.174 42.3272C210.465 43.0363 209.959 43.9224 209.708 44.8934L205.27 62.1068C205.187 62.4016 205.01 62.6613 204.766 62.8464C204.523 63.0314 204.225 63.1316 203.919 63.1316C203.612 63.1316 203.315 63.0314 203.071 62.8464C202.827 62.6613 202.65 62.4016 202.568 62.1068L198.132 44.8934Z"
            fill="white"
          />
        </svg>
      </div>
    </button>
  </Transition>
</template>

<style scoped>
  .launcher-button {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    padding: 4px;
    border: none;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
    transition: transform 0.2s ease;
    outline: none;
  }

  .launcher-button:focus-visible {
    outline: 3px solid currentColor;
    outline-offset: 2px;
    border-radius: 50%;
  }

  /*
   * Pulsing glow — breathing drop-shadow that draws the user's eye.
   * Runs continuously while the widget is closed (pending actions exist).
   */
  .launcher-button:not(.launcher-button--widget-open) {
    animation: pulse-glow 2.4s ease-in-out infinite;
  }

  /* Hover overrides the pulse with a steady elevated shadow */
  .launcher-button--hovered:not(.launcher-button--widget-open) {
    animation: none;
    filter: drop-shadow(0 8px 24px rgba(168, 85, 247, 0.55));
  }

  .launcher-button--widget-open {
    filter: none;
    animation: none;
  }

  /*
   * Inner circle — 56x56, holds the rotating ring + purple fill.
   * The linear gradient here is the hover/press background; it's
   * normally hidden behind the ::before linear gradient layer.
   */
  .launcher-button__inner {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    overflow: hidden;
    background: linear-gradient(to right, #ef469a, #8526c2);
    transition: transform 0.1s ease;
  }

  /* Hover: scale up (451 → 481 in Figma ≈ 1.066) */
  .launcher-button--hovered:not(.launcher-button--pressed) .launcher-button__inner {
    transform: scale(1.066);
  }

  /* Active press: snap back to base size from hover-enlarged state */
  .launcher-button--pressed .launcher-button__inner {
    transform: scale(1);
  }

  /*
   * Rotating gradient ring — a static linear gradient on a rotating
   * pseudo-element. The Figma design defines 4 variants (right, bottom,
   * left, top) representing animation frames; rotating the element
   * produces the same sweep smoothly and continuously.
   */
  .launcher-button__inner::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: linear-gradient(to right, #ef469a, #8526c2);
    animation: ring-rotate 1.5s linear infinite;
    transition: opacity 0.2s ease;
  }

  /* Fade out ring on hover — reveals the static linear gradient beneath */
  .launcher-button--hovered .launcher-button__inner::before {
    opacity: 0;
  }

  /* Hide ring when widget is open — button keeps its size but no rotating ring */
  .launcher-button--widget-open .launcher-button__inner::before {
    opacity: 0;
  }

  /* Expand purple fill to cover the full circle when widget is open (no ring gap) */
  .launcher-button--widget-open .launcher-button__inner::after {
    inset: 0;
  }

  /*
   * Purple fill — sits on top of the gradient layers.
   * Default: inset 4px → only 4px of gradient visible as a ring.
   * Hover/press: inset 0 + lighter overlay → full gradient visible.
   */
  .launcher-button__inner::after {
    content: '';
    position: absolute;
    inset: 4px;
    border-radius: 50%;
    background: rgba(106, 22, 181, 0.5);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    transition:
      inset 0.2s ease,
      background-color 0.2s ease;
  }

  .launcher-button--hovered .launcher-button__inner::after,
  .launcher-button--pressed .launcher-button__inner::after {
    inset: 0;
    background: rgba(106, 22, 181, 0.2);
  }

  @keyframes ring-rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes pulse-glow {
    0%,
    100% {
      filter: drop-shadow(0 6px 16px rgba(106, 22, 181, 0.4));
    }
    50% {
      filter: drop-shadow(0 4px 24px rgba(168, 85, 247, 0.7))
        drop-shadow(0 0 10px rgba(217, 70, 239, 0.3));
    }
  }

  /* Icon — 34x34, white, positioned above pseudo-elements */
  .launcher-button__icon {
    position: relative;
    z-index: 1;
    width: 34px;
    height: 34px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.12));
  }

  /* Entrance / exit transition */
  .launcher-fade-enter-active {
    transition:
      opacity 0.3s ease,
      transform 0.3s ease;
  }

  .launcher-fade-leave-active {
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;
  }

  .launcher-fade-enter-from,
  .launcher-fade-leave-to {
    opacity: 0;
    transform: scale(0.5) translateY(20px);
  }
</style>
